"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ModalRegistry, ModalDataFor } from "@/lib/modals/data/modal-data-types";
import type { BuildActionContextParams, DetailActionContext } from "./types";

/**
 * Read a foreign-key field off an entity row as a string id.
 *
 * Every field except `id` reaches us through `BaseEntity`'s index signature,
 * which is deliberately wide (a CRM row carries strings, numbers, arrays and
 * nested relations). `DetailActionContext["entityIds"]` is all
 * `string | undefined`, so narrow here instead of asserting upstream.
 */
function readIdField(source: BaseEntity, field: string): string | undefined {
  const value: unknown = source[field];
  return typeof value === "string" ? value : undefined;
}

export function useBuildActionContext<T extends BaseEntity>({
  item,
  hooks,
  refetch,
  updateMutation,
  deleteMutation,
  entityName,
  config,
  customComponents,
  onEditModalOpen,
  onEditModalClose,
  onDeleteComplete,
  isTicketInlineEditMode,
  onToggleTicketInlineEdit,
  hasTicketPendingChanges,
  onSaveTicketPending,
  isTaskInlineEditMode,
  onToggleTaskInlineEdit,
  hasTaskPendingChanges,
  onSaveTaskPending,
}: BuildActionContextParams<T>): DetailActionContext<T> | null {
  // Always call hooks at the top level - no conditional logic
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useGlobalModalStack();

  // Annotated so the returned object literal is CONTEXTUALLY typed by the
  // contract: callback parameters below get their types from
  // `DetailActionContext<T>` instead of being re-declared (which is where the
  // `Record<string, any>` on `onOpenModal` came from), and a missing or
  // misspelled member is an error here rather than at the call site.
  return useMemo<DetailActionContext<T> | null>(() => {
    // Return null if no item, but hooks are still called consistently
    if (!item) return null;

    // Extract entity IDs from the item.
    //
    // `BaseEntity`'s index signature types every non-`id` field as the broad
    // `FieldValue | Record<string, unknown> | unknown[] | undefined` union, so
    // these reads are narrowed at the boundary rather than asserted: every
    // field of `entityIds` is `string | undefined`, and a non-string value is
    // not an id. (The previous `(item as any).x` reads would have passed a
    // number or an object straight through into a `string` field.)
    const entityIds = {
      contactId:
        readIdField(item, "contact_id") ||
        item.id ||
        (entityName === "contacts" ? item.id : undefined),
      ticketId: readIdField(item, "ticket_id") || (entityName === "tickets" ? item.id : undefined),
      companyId:
        readIdField(item, "company_id") || (entityName === "companies" ? item.id : undefined),
      employeeId:
        readIdField(item, "employee_id") || (entityName === "employees" ? item.id : undefined),
      userId: readIdField(item, "user_id") || (entityName === "users" ? item.id : undefined),
      taskId: readIdField(item, "task_id") || (entityName === "tasks" ? item.id : undefined),
    };

    return {
      pageConfig: {
        entityName,
        config,
        hooks,
        customComponents,
      },
      item,
      entityName,
      entityIds,
      onRefresh: () => refetch(),
      onNavigate: (href: string) => router.push(href),
      onToast: (message: string, type: "success" | "error" | "info" | "warning" = "info") => {
        toast[type](message);
      },
      onOpenModal: (modalId, data) => {
        if (modalId === "edit") {
          onEditModalOpen?.();
        } else {
          openModal(modalId, data);
        }
      },
      openModalTypeSafe: <T extends keyof ModalRegistry>(modalId: T, data?: ModalDataFor<T>) => {
        if (modalId === "edit") {
          onEditModalOpen?.();
        } else {
          openModal(modalId, data);
        }
      },
      onCloseModal: (modalId: string) => {
        if (modalId === "edit") {
          onEditModalClose?.();
        } else {
          closeModal(modalId);
        }
      },
      onUpdateItem: async (updates: Partial<T>) => {
        if (!item) throw new Error("No item to update");

        // Use EntityHooks interface - always use mutateAsync
        await updateMutation.mutateAsync({
          ...updates,
          id: item.id,
        });

        await refetch();
      },
      onDeleteItem: async () => {
        if (!item) throw new Error("No item to delete");

        // Use EntityHooks interface - always use mutateAsync
        await deleteMutation.mutateAsync(item.id);

        // Call the callback if provided
        if (onDeleteComplete) {
          onDeleteComplete();
        } else {
          // Default behavior: navigate back to list
          router.push(`/${entityName}`);
        }
      },
      isTicketInlineEditMode,
      onToggleTicketInlineEdit,
      hasTicketPendingChanges,
      onSaveTicketPending,
      isTaskInlineEditMode,
      onToggleTaskInlineEdit,
      hasTaskPendingChanges,
      onSaveTaskPending,
      queryClient,
    };
  }, [
    item?.id,
    hooks,
    refetch,
    router,
    entityName,
    config,
    customComponents,
    openModal,
    closeModal,
    updateMutation,
    deleteMutation,
    onEditModalOpen,
    onEditModalClose,
    onDeleteComplete,
    isTicketInlineEditMode,
    onToggleTicketInlineEdit,
    hasTicketPendingChanges,
    onSaveTicketPending,
    isTaskInlineEditMode,
    onToggleTaskInlineEdit,
    hasTaskPendingChanges,
    onSaveTaskPending,
    queryClient,
    // Note: entityIds are derived from item, so we don't need to add them to deps
  ]);
}
